import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { resetPassword, clearError, clearResetPasswordSent } from '../store/authSlice'

export default function ForgotPasswordForm() {
  const [email, setEmail] = useState('')
  const [formErrors, setFormErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const dispatch = useDispatch()
  const { error, resetPasswordSent } = useSelector((state) => state.auth)

  const validateForm = () => {
    const errors = {}

    if (!email.trim()) {
      errors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is invalid'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    dispatch(clearError())

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    try {
      await dispatch(resetPassword(email)).unwrap()
    } catch (err) {
      // Error is handled by Redux state, just stop the loading spinner
      // The error is already set in authSlice via rejectWithValue
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBackToLogin = () => {
    dispatch(clearResetPasswordSent())
    dispatch(clearError())
  }

  // Loading state - sending reset email
  if (isSubmitting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4 py-8 font-system light-mode-only">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="apple-card animate-slide-in px-5 py-8">
            <div className="text-center space-y-4">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="apple-text-body text-text-muted">Sending reset link...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Success state - email sent
  if (resetPasswordSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4 py-8 font-system light-mode-only">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="apple-card animate-slide-in px-5 py-8">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-status-success rounded-full flex items-center justify-center mx-auto">
                <CheckCircleIcon className="w-6 h-6 text-white" />
              </div>
              <h1 className="apple-text-title">Check your email</h1>
              <p className="apple-text-body text-text-muted">
                We've sent a password reset link to{' '}
                <span className="font-medium text-text-primary">{email}</span>
              </p>
              <p className="apple-text-body text-text-muted">
                Didn't receive the email? Check your spam folder or{' '}
                <button
                  onClick={() => dispatch(clearResetPasswordSent())}
                  className="text-accent-foreground hover:text-status-info font-medium transition-colors"
                >
                  try again
                </button>
              </p>
            </div>
            <Link
              to="/login"
              onClick={handleBackToLogin}
              className="apple-button-secondary w-full flex items-center justify-center mt-6"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" aria-hidden="true" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Request form state
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4 py-8 font-system light-mode-only">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="apple-card animate-slide-in px-5 py-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="apple-text-title">Reset your password</h1>
            <p className="apple-text-body text-text-muted mt-2">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-accent-danger border border-status-danger rounded-lg p-3 mb-6">
              <p className="text-sm text-status-danger">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-label-secondary">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={`apple-input ${
                  formErrors.email ? 'apple-input-error' : ''
                }`}
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (formErrors.email) {
                    setFormErrors(prev => ({ ...prev, email: '' }))
                  }
                }}
                disabled={isSubmitting}
              />
              {formErrors.email && (
                <p className="text-xs text-status-danger mt-1">{formErrors.email}</p>
              )}
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="apple-button-primary w-full flex items-center justify-center focus-apple disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send reset link
              </button>

              <Link
                to="/login"
                onClick={handleBackToLogin}
                className="apple-button-secondary w-full flex items-center justify-center"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                Back to sign in
              </Link>
            </div>
          </form>
        </div>

        {/* Sign Up Link */}
        <div className="text-center mt-6">
          <p className="apple-text-body">
            Don't have an account?{' '}
            <Link
              to="/signup"
              className="text-accent-foreground hover:text-status-info font-medium transition-colors"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
